import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import AppLayout from "../layout/AppLayout";
import { coreRoutes } from "./core_routes";
import { customRoutesAll } from "./custom_routes_all";
import { customRoutes } from "@custom/custom_routes";
import ProtectedRoute from "./ProtectedRoute";
import SeoNative from "@components/seo/Seo";
import { APP_NAME } from "@config";
import PermissionGuard from "@utils/PermissionGuard";
import { extractModuleKeyFromPath } from "@utils/usePermission"; // ✅ helper we'll add

const Login = lazy(() => import("@plugin/auth/views/LoginPageView"));
const ForgotPasswordPage = lazy(() => import("@plugin/auth/views/ForgotPasswordView"));
const VerifyAccountPage = lazy(() => import("@plugin/auth/views/VerifyAccountView"));

const allRoutes = [...customRoutesAll, ...customRoutes, ...coreRoutes];

const RouteWrapper = ({ element, seo }) => (
  <>
    {seo && <SeoNative fallback={seo} />}
    {element}
  </>
);

const renderWithGuards = (element, seo, moduleKey, action, skipPermission) => {
  if (skipPermission) {
    return (
      <ProtectedRoute>
        <RouteWrapper element={element} seo={seo} />
      </ProtectedRoute>
    );
  }
  return (
    <ProtectedRoute>
      <PermissionGuard module={moduleKey} action={action}>
        <RouteWrapper element={element} seo={seo} />
      </PermissionGuard>
    </ProtectedRoute>
  );
};


const RouteConfig = () => (
  <Suspense fallback={<div className="text-center p-10">Loading...</div>}>
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <>
            <SeoNative
              fallback={{
                title: "Login | Webtrix24 CRM",
                description: `Securely log in to your ${APP_NAME} account to manage inventory, contracts, and invoices.`,
              }}
            />
            <Login />
          </>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <>
            <SeoNative
              fallback={{
                title: "Forgot Password | Webtrix24 CRM",
                description: `Reset your ${APP_NAME} password securely and regain access.`,
              }}
            />
            <ForgotPasswordPage />
          </>
        }
      />
      <Route
        path="/verify-details"
        element={
          <>
            <SeoNative
              fallback={{
                title: "Verify Account | Webtrix24 CRM",
                description: `Verify your ${APP_NAME} account to activate secure access.`,
              }}
            />
            <VerifyAccountPage />
          </>
        }
      />

      {/* Protected Routes */}
      <Route element={<AppLayout />}>
        {allRoutes.map(({ path, element, seo, permission, skipPermission }, i) => {
          const moduleKey = permission?.module || extractModuleKeyFromPath(path);
          const action = permission?.action || "view";
          return (
            <Route
              key={i}
              path={path}
              element={renderWithGuards(element, seo, moduleKey, action, skipPermission)}
            />
          );
        })}
      </Route>
      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/login" />} />
    </Routes>
  </Suspense>
);

export default RouteConfig;
